import Section from '../components/shared/Section'
import Card from '../components/shared/Card'
import Button from '../components/shared/Button'
import SEO from '../components/shared/SEO'
import { ROUTE_META } from '../lib/seo'

export default function About() {
  const careerHighlights = [
    {
      org: "Compaq",
      period: "1998–2001",
      body: "Joined as a customer response agent, promoted four times in three years. Built OpsLink — a platform connecting Compaq's channel partners (including Computacenter) into a single operational system, replacing fragmented communication with shared visibility. Also built the Direct Sales order management system, a forecasting tool for the Finance Director, and a $16M order risk early-warning system. None of these were asked for. I saw the gap, designed the solution, and pitched it upward.",
    },
    {
      org: "American Express",
      period: "2006–2018",
      body: "Fifteen years across four roles. Built the CATS CRM to bring visibility to merchant certification. Owned $6.7bn of monthly EMEA transaction processing. Delivered PSD2 compliance across 56 countries. Ran a KYC programme verifying 20,000 merchant accounts a year — and consolidated 11 fragmented data providers into one.",
    },
    {
      org: "TalkWebSolutions",
      period: "2008–2010",
      body: "Worked as right hand to the founder of an early-stage web development company. Won and built POEM — a first-to-market clinical trial management system for the NHS, used by Great Ormond Street Hospital. Faster treatments. Lower NHS costs.",
    },
    {
      org: "Bluechain",
      period: "2023–2026",
      body: "Joined as Lead Service Designer and progressed to COO. Bluechain was building genuinely innovative payment infrastructure — Request to Pay, combining invoice and payment in a single digital object — in a space where others had tried and failed. Partnerships with American Express and Visa Europe. Helped grow the business from 3 to 16 people, 0 to 3,000 customers, processing £9M+ a month in transactions.",
    },
  ]

  const credentials = [
    "Six Sigma Black Belt — Compaq · 2001",
    "Product Design — Central St Martins, London · 1999",
    "Agile Scrum Master & Agile Coaching — Skills Matter, London · 2010",
    "UX / UI Design (6 months, full time) — Flatiron School, London · 2019–2020",
    "Service Design Practitioner (SDN Accredited) — SDN Academy · 2022",
    "d.MBA — Business for Designers · 2024",
  ]

  return (
    <>
      <SEO
        title={ROUTE_META['/about'].title}
        description={ROUTE_META['/about'].description}
        jsonLd={ROUTE_META['/about'].jsonLd}
      />

      {/* Hero Section */}
      <Section>
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl text-neon-pink mb-6">
            30 years designing better relationships between people, services and systems.
          </h1>
          <p className="text-xl text-neutral-400">
            Alexander Miller — the person behind Mathos Consult.
          </p>
        </div>
      </Section>

      {/* Origin Story Section */}
      <Section background="gray">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl text-neon-orange mb-8">Where This Started</h2>
          <div className="space-y-6">
            <p className="text-xl text-neutral-400 leading-relaxed">
              I began my career at Compaq as a business process analyst, trained as a Six Sigma Black Belt and focused on operational efficiency for large organisations. The work was analytical, structured, and heavily data-driven. We mapped processes, measured performance, identified bottlenecks, and designed technically rigorous solutions.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              On paper, many of those solutions were successful.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              But I kept encountering the same problem: systems that worked operationally were still failing the people expected to use them. Not because the analysis was wrong, but because the solution had often been defined before anyone properly understood the lived experience of the people involved.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              We optimised processes without fully understanding human context.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              That realisation fundamentally changed how I approached design and problem solving. I began focusing not only on systems and operations, but also on users, behaviours, environments, and human needs. Over time this led me into UX, human-centred design, service design, and later broader business strategy through a d.MBA.
            </p>
            <p className="text-xl text-neutral-300 leading-relaxed">
              What stayed consistent throughout was the systems thinking and operational rigour. What changed was the scope of what I considered part of the problem.
            </p>
          </div>
        </div>
      </Section>

      {/* Career Highlights Section */}
      <Section>
        <h2 className="text-4xl text-center text-neon-lime mb-12">
          Career highlights.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {careerHighlights.map((highlight, index) => {
            const colors = ['neon-lime', 'neon-pink', 'neon-orange', 'neon-purple']
            const color = colors[index % colors.length]
            return (
              <Card key={index}>
                <div className="flex items-baseline gap-3 mb-4">
                  <h3 className={`text-2xl text-${color}`}>{highlight.org}</h3>
                  <span className="text-neutral-500 text-sm">{highlight.period}</span>
                </div>
                <p className="text-neutral-400 leading-relaxed">
                  {highlight.body}
                </p>
              </Card>
            )
          })}
        </div>
      </Section>

      {/* How I Think About Problems Section */}
      <Section background="gray">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl text-neon-purple mb-8">How I Think About Problems</h2>
          <div className="space-y-6">
            <p className="text-xl text-neutral-400 leading-relaxed">
              Across thirty years of work, a consistent pattern has emerged: organisations often lose visibility of the real experiences people are having inside the systems they create.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              Processes become fragmented. Teams optimise for their own measures of success. Services become organised around operational structures rather than human outcomes. Over time, the gap between how organisations think something works and how people actually experience it can become surprisingly large.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              Much of my work involves helping organisations reconnect those two perspectives.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              That can mean:
            </p>
            <ul className="space-y-3 ml-1">
              {[
                'identifying friction people have normalised,',
                'uncovering operational blind spots,',
                'reframing problems before solutions are designed,',
                'or helping teams see relationships across systems that are usually treated separately.',
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-lg text-neutral-400"
                >
                  <span className="text-neon-lime mt-1 shrink-0">—</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-xl text-neutral-300 leading-relaxed">
              When organisations can clearly see what is really happening — operationally and experientially — better decisions tend to follow naturally. That often leads to simpler systems, reduced friction, lower costs, better customer experiences, and more meaningful outcomes for the people involved.
            </p>
          </div>
        </div>
      </Section>

      {/* Credentials Section */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl text-center text-neon-orange mb-10">
            Credentials.
          </h2>
          <ul className="space-y-4">
            {credentials.map((credential, index) => (
              <li key={index} className="flex items-start gap-3 text-lg text-neutral-400">
                <span className="text-neon-lime mt-1 shrink-0">—</span>
                <span>{credential}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="gray">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-2xl md:text-3xl text-neutral-100 mb-8 leading-relaxed">
            I work with people who are building something genuinely better. Wholesome, not extractive.
          </p>
          <Button variant="primary" size="large" onClick={() => window.location.href = '/contact#booking'}>
            Let's talk about what you're building
          </Button>
        </div>
      </Section>
    </>
  )
}
